import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {useMutation} from "@repo/data-gql";
import {RequestInfo} from "@repo/shared-core";

import type {
    unlinkAuthEmailType,
    unlinkAuthEmail_unlinkEmail_error as UnlinkEmailError,
} from "./__graphql-types__/unlinkAuthEmail.ts";
import ConnectEmail from "./connect-email.tsx";
import {UNLINK_EMAIL_MUTATION} from "./unlink-email-mutation.ts";

import EnvelopeSimpleIcon from "@phosphor-icons/core/regular/envelope-simple.svg";
import minusCircleIcon from "@phosphor-icons/core/regular/minus-circle.svg";

type ErrorCode = UnlinkEmailError["code"];
type Data = unlinkAuthEmailType["response"];

const ERROR_CODE_MESSAGE_MAPPING: Partial<
    Record<ErrorCode, (arg1: string) => string>
> = {
    EMAIL_ISNT_LINKED_TO_ACCOUNT: (email: string) =>
        t`The email address ${email} isn't linked to your Khan Academy account`,
    MUST_SET_PRIMARY_EMAIL_BEFORE_REMOVING: (email: string) =>
        t`Please set your primary email address to be a different address before removing ${email}.`,
    CANNOT_REMOVE_PRIMARY_EMAIL: (email: string) =>
        t`Please set your primary email address to be a different address before removing ${email}.`,
};

const strings = {
    confirmDisconnectEmail: (email: string) =>
        t`Are you sure you want to remove ${email} from your account?`,

    noEmailModifyingChild: (age: number) => {
        const newEnString = `There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your child's email address can be added in their settings page, so they can receive notifications.`;
        const newI18nString = t`There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your child's email address can be added in their settings page, so they can receive notifications.`;
        if (RequestInfo.kaLocale === "en" || newEnString !== newI18nString) {
            return newI18nString;
        }
        // TODO(somewhatabstract, TR-1250): Remove this old string once all
        // locales have translations for the new string above. Check the
        // translations portal or with the translations team to be sure.
        return t`There is no email address connected to this Khan Academy account. Since your child is ${age} years old, your two accounts can be unlinked by adding an email address by going to this settings page from your child's account.`;
    },

    noEmailNotModifyingChild: (age: number) => {
        const newEnString = `You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications. Your linked parent will continue to be your coach unless you edit your list of coaches from your profile page.`;
        const newI18nString = t`You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account so you can receive email notifications. Your linked parent will continue to be your coach unless you edit your list of coaches from your profile page.`;
        if (RequestInfo.kaLocale === "en" || newEnString !== newI18nString) {
            return newI18nString;
        }
        // TODO(somewhatabstract, TR-1250): Remove this old string once all
        // locales have translations for the new string above. Check the
        // translations portal or with the translations team to be sure.
        return t`You don't have an email address connected to this Khan Academy account. Since you're ${age} years old, you can connect an email address to your account, which will unlink it from your parent's account.`;
    },
};

interface Props {
    age: number | null | undefined;
    authEmails: Array<string> | null | undefined;
    hasPassword: boolean;
    isModifyingChild: boolean;
    identifier: string | null | undefined;
}

export const LinkEmail: React.FC<Props> = ({
    age,
    authEmails,
    hasPassword,
    isModifyingChild,
    identifier,
}) => {
    const [disconnectEmailSuccessMessage, setDisconnectEmailSuccessMessage] =
        React.useState("");
    const [unlinkEmailMutation, result] = useMutation(UNLINK_EMAIL_MUTATION);

    const handleMutationCompletion = (result: Data, email: string) => {
        if (result?.unlinkEmail && !result.unlinkEmail.error) {
            setDisconnectEmailSuccessMessage(
                t`Successfully removed ${email} from your Khan Academy account.`,
            );
        }
    };

    const renderEmailAndDisconnect = (email: string, emailsLength: number) => {
        let errorMessage: string | null = null;

        if (result?.status === "error") {
            errorMessage = t`Oops. Something went wrong. Please try again.`;
        }

        if (
            result?.status === "success" &&
            result.data.unlinkEmail?.error?.code
        ) {
            const errorFunction =
                ERROR_CODE_MESSAGE_MAPPING[result.data.unlinkEmail.error.code];
            errorMessage = errorFunction
                ? errorFunction(email)
                : t`Oops. Something went wrong. Please try again.`;
        }

        const handleOnClickDisconnect = async () => {
            // eslint-disable-next-line no-alert -- allow alerts
            if (!confirm(strings.confirmDisconnectEmail(email))) {
                return;
            }

            const mutationResult = await unlinkEmailMutation({
                authEmail: email,
            });
            if (mutationResult.status === "success") {
                handleMutationCompletion(mutationResult.data, email);
            }
        };

        return (
            <View key={email}>
                <View style={[styles.row, styles.thirdPartyButtonContainer]}>
                    <LabelMedium style={[styles.verticalCenter, styles.label]}>
                        {email}
                    </LabelMedium>
                    <Strut size={spacing.small_12} />
                    {emailsLength > 1 &&
                        (RequestInfo.isPhone ? (
                            <>
                                <Spring />
                                <IconButton
                                    actionType="destructive"
                                    icon={minusCircleIcon}
                                    aria-label={t`Disconnect`}
                                    onClick={handleOnClickDisconnect}
                                />
                            </>
                        ) : (
                            <Button
                                onClick={handleOnClickDisconnect}
                                kind="tertiary"
                                spinner={result?.status === "loading"}
                                style={styles.disconnectButton}
                            >
                                {t`Disconnect`}
                            </Button>
                        ))}
                </View>
                {errorMessage && (
                    <Body style={styles.errorMessage}>{errorMessage}</Body>
                )}
            </View>
        );
    };

    const renderLinkedEmails = () => {
        if (authEmails?.length) {
            return (
                <View style={styles.column}>
                    {authEmails.map((email) =>
                        renderEmailAndDisconnect(email, authEmails.length),
                    )}
                    {disconnectEmailSuccessMessage && (
                        <Body style={styles.successMessage}>
                            {disconnectEmailSuccessMessage}
                        </Body>
                    )}
                </View>
            );
        }
        if (age) {
            // If there is no email, then either a parent is modifying a child or
            // a child aged up and still hasn't added an email. */}
            if (isModifyingChild) {
                return (
                    <View>
                        <Body>{strings.noEmailModifyingChild(age)}</Body>
                        <Body>
                            {t`You will continue to be your child's coach unless your account is manually removed from the list of coaches.`}
                        </Body>
                    </View>
                );
            }
            return (
                <View>
                    <Body>{strings.noEmailNotModifyingChild(age)}</Body>
                    <Body>
                        {t`Your parent will continue to be your coach unless you edit your list of coaches from your profile page.`}
                    </Body>
                </View>
            );
        }
        return null;
    };

    const renderConnectEmail = () => {
        return (
            <View style={styles.connectEmailButton}>
                <ConnectEmail
                    buttonText={
                        authEmails?.length
                            ? t`Connect another email`
                            : t`Connect an email`
                    }
                    identifier={identifier}
                />
            </View>
        );
    };

    return (
        <View>
            <View style={styles.row}>
                <View
                    style={[
                        styles.row,
                        styles.thirdPartyButtonContainer,
                        styles.alignLabels,
                    ]}
                >
                    {/* Email icon needs a touch of pixel nudging down to
                        look visually centered vertically. */}
                    <View
                        style={[
                            styles.verticalCenter,
                            styles.thirdPartyIcon,
                            {paddingBlockStart: "1px"},
                        ]}
                    >
                        <PhosphorIcon
                            aria-hidden={true}
                            icon={EnvelopeSimpleIcon}
                            size="small"
                        />
                    </View>
                    <LabelMedium style={[styles.verticalCenter, styles.label]}>
                        {t`Email`}
                    </LabelMedium>
                </View>
                <Strut size={spacing.medium_16} />
                {renderLinkedEmails()}
            </View>
            <Strut size={spacing.small_12} />
            {/* We only allow users to connect an email if they set a password first,
                otherwise it's too easy to get locked out. */}
            {!isModifyingChild &&
                (hasPassword ? (
                    renderConnectEmail()
                ) : (
                    <Body tag="em">
                        {t`To connect another email address to your Khan Academy account, first add a password to your account in the "Password" section above.`}
                    </Body>
                ))}
        </View>
    );
};

const styles = StyleSheet.create({
    row: {
        alignItems: "center",
        flexDirection: "row",
    },
    column: {
        flexDirection: "column",
        height: "fit-content",
    },
    verticalCenter: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    thirdPartyIcon: {
        margin: spacing.xSmall_8,
        width: spacing.medium_16,
        height: spacing.medium_16,
    },
    thirdPartyButtonContainer: {
        height: spacing.xLarge_32,
        display: "flex",
        alignItems: "center",
    },
    connectEmailButton: {
        paddingInlineStart: 116,
    },
    alignLabels: {
        flex: "0 0 100px",
    },
    label: {
        color: color.offBlack,
    },
    errorMessage: {
        color: color.red,
    },
    successMessage: {
        color: color.green,
    },
    disconnectButton: {
        height: spacing.xLarge_32,
    },
});
export default LinkEmail;
